import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

type CommandPaletteShape = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const CommandPaletteContext = createContext<CommandPaletteShape | undefined>(undefined);

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);

  return React.createElement(
    CommandPaletteContext.Provider,
    { value: { isOpen, open, close, toggle } },
    children
  );
};

export const useCommandPalette = (): CommandPaletteShape => {
  const ctx = useContext(CommandPaletteContext);

  // Local fallback so the hook still works if Provider isn't used.
  const [localOpen, setLocalOpen] = useState(false);
  const openLocal = useCallback(() => setLocalOpen(true), []);
  const closeLocal = useCallback(() => setLocalOpen(false), []);
  const toggleLocal = useCallback(() => setLocalOpen((p) => !p), []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggleLocal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleLocal]);

  if (ctx) return ctx;

  return {
    isOpen: localOpen,
    open: openLocal,
    close: closeLocal,
    toggle: toggleLocal,
  };
};
